import { PublicClientApplication } from '@azure/msal-browser';
import {
  loginRequest,
  msalConfigB2C,
  msalConfigB2E,
} from '@configs/authConfig';
import { removeAuthResponseHash } from '@shared/helpers/auth-response-hash/removeAuthResponseHash';
import { clearMsalCookies } from '@shared/helpers/msal-instance/msalInstance.utils';

import { AuthType } from '../auth-provider/AuthProvider.types';

export async function createMsalInstance(
  authType: AuthType
): Promise<PublicClientApplication> {
  const config = authType === 'b2e' ? msalConfigB2E : msalConfigB2C;
  const instance = new PublicClientApplication(config);
  await instance.initialize();
  return instance;
}

/**
 * A failed redirect leaves the old response fragment in the address bar; if it stays,
 * the next login starts from it and fails again with `state_mismatch`.
 */
export function clearAuthResponseHashFromLocation(): void {
  const { href } = globalThis.location;
  const cleanUrl = removeAuthResponseHash(href);
  if (cleanUrl !== href) {
    globalThis.history.replaceState(globalThis.history.state, '', cleanUrl);
  }
}

/**
 * Clear MSAL interaction status from all storage layers.
 * When switching auth types (e.g. B2E logout → B2C login), the previous
 * instance's interaction state persists in sessionStorage because MSAL only
 * clears its own clientId's entries. This manual cleanup prevents
 * BrowserAuthError: interaction_in_progress.
 *
 * NOTE: This intentionally does NOT call `clearMsalStorage()` because that
 * also removes cached tokens/accounts from localStorage. During login we
 * only want to clear interaction state, not the token cache.
 */
function isInteractionStatusKey(key: string): boolean {
  return (
    key.includes('interaction_status') || key.includes('interaction.status')
  );
}

function clearInteractionStatus(): void {
  Object.keys(sessionStorage)
    .filter(key => key.startsWith('msal.') || isInteractionStatusKey(key))
    .forEach(key => sessionStorage.removeItem(key));
  Object.keys(localStorage)
    .filter(isInteractionStatusKey)
    .forEach(key => localStorage.removeItem(key));
  clearMsalCookies({ preserveEncryptionKey: true });
}

function isInteractionInProgressError(error: unknown): boolean {
  if (typeof error !== 'object' || error === null) {
    return false;
  }

  const errorCode = (error as { errorCode?: string }).errorCode;
  return errorCode === 'interaction_in_progress';
}

export async function loginWithRedirect(
  instance: PublicClientApplication | null,
  deepLinkUrl?: string,
  loginHint?: string
): Promise<void> {
  if (!instance) return;

  const request = {
    ...loginRequest,
    ...(loginHint ? { loginHint } : {}),
    redirectStartPage: removeAuthResponseHash(globalThis.location.href),
  };

  await instance.handleRedirectPromise();
  clearInteractionStatus();

  try {
    if (deepLinkUrl) {
      sessionStorage.setItem('auth-deep-link', deepLinkUrl);
    }
    await instance.loginRedirect(request);
  } catch (error) {
    if (isInteractionInProgressError(error)) {
      clearInteractionStatus();

      try {
        await instance.loginRedirect(request);
        return;
      } catch (retryError) {
        console.error('MSAL loginRedirect retry failed:', retryError);
        throw retryError;
      }
    }

    console.error('MSAL loginRedirect failed:', error);
    throw error;
  }
}
